import { fetchLearnLibrary } from '@api/taptotip';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { LearnBlocks, learnModulePath } from '@components/TapToTip/GuideAdvice';
import { StyleBreakpoints } from '@constants';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

// Card 119 — "Guide advice in dashboard". Card 211 renamed the section to
// "FAQ"; the route, the tab id and the file/component names still say `learn`
// because they are not user-facing and the dashboard card deep-links into them.
//
// The library, plus the reader for a single module. Content comes from the API
// rather than this bundle so copy edits do not require a release of the web app
// and the Expo app in lockstep, and so the two can never drift apart.
//
// `?module=<id>` selects the reader. It is a query param rather than a nested
// route so the dashboard card, and eventually an email, can deep-link straight
// to a module without adding a second entry to the route table (which sits
// above a `/tipdirect/:tab` catch-all and is easy to get wrong).

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const CARD = 'w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0';

const LoadingSkeleton = () => (
  // Shaped like the library it replaces, so the page does not jump when the
  // real content arrives.
  <div className="flex flex-col gap-8 w-full" aria-label="Loading FAQs" role="status">
    {[0, 1].map(track => (
      <div key={track} className="flex flex-col gap-4">
        <div className="h-5 w-40 bg-slate-100 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1].map(card => (
            <div key={card} className={`${CARD} animate-pulse`}>
              <div className="h-4 w-3/4 bg-slate-100 rounded" />
              <div className="h-3 w-full bg-slate-100 rounded mt-3" />
              <div className="h-3 w-5/6 bg-slate-100 rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ModuleCard = ({ module, onOpen, pinned }) => (
  <button
    type="button"
    onClick={() => onOpen(module.id)}
    className={`${CARD} text-left cursor-pointer hover:border-slate-300 active:translate-y-px transition-colors ${
      pinned ? '!border-indigo-200 !bg-indigo-50/40' : ''
    }`}
  >
    {pinned && (
      <span className="inline-block text-[10px] font-bold px-2 py-1 rounded-lg border border-solid uppercase tracking-wider bg-indigo-50 text-indigo-700 border-indigo-100 mb-3">
        Start here
      </span>
    )}
    <div className="text-sm font-black text-slate-900">{module.title}</div>
    <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">{module.summary}</p>
    <div className="text-xs text-slate-400 font-semibold mt-3">{module.readMinutes} min read</div>
  </button>
);

ModuleCard.propTypes = {
  module: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  pinned: PropTypes.bool
};

// No `user` prop: who may reach this page is decided by PrivateRoute and the nav
// gate, and what they see is decided by the API (payments companies get an empty
// library). Reading the redux user here would just be a fourth place to keep the
// same rule in sync.
const Learn = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [library, setLibrary] = useState(null);
  const [error, setError] = useState(null);

  const selectedId = new URLSearchParams(location.search).get('module');

  useEffect(() => {
    let cancelled = false;
    fetchLearnLibrary()
      .then(data => {
        if (!cancelled) setLibrary(data);
      })
      .catch(err => {
        if (!cancelled) setError(err && err.message ? err.message : 'Something went wrong.');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const openModule = moduleId => history.push(learnModulePath(moduleId));

  const modules = (library && library.modules) || [];
  const selected = selectedId ? modules.find(item => item.id === selectedId) : null;

  const renderBody = () => {
    if (error) {
      return (
        <div className={`${CARD} border-amber-200 bg-amber-50`}>
          <div className="text-sm font-bold text-amber-900">We could not load the FAQs</div>
          <p className="text-sm text-amber-800 leading-relaxed m-0 mt-1">{error}</p>
        </div>
      );
    }

    if (!library) return <LoadingSkeleton />;

    // Payments companies get an empty library from the API rather than an error,
    // so a deep link lands somewhere quiet instead of on a toast.
    if (modules.length === 0) {
      return (
        <div className={CARD}>
          <div className="text-sm font-bold text-slate-900">Nothing here yet</div>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">
            There are no FAQs for your account right now. Check back soon.
          </p>
        </div>
      );
    }

    if (selectedId && !selected) {
      return (
        <div className={CARD}>
          <div className="text-sm font-bold text-slate-900">We could not find that answer</div>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">
            It may have been renamed. Everything else is listed below.
          </p>
          <button
            type="button"
            onClick={() => history.push('/tipdirect/learn')}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors"
          >
            Back to all FAQs
          </button>
        </div>
      );
    }

    if (selected) {
      return (
        <article className={CARD} data-testid="learn-module">
          <button
            type="button"
            onClick={() => history.push('/tipdirect/learn')}
            className="bg-transparent border-0 p-0 mb-4 text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
          >
            &larr; All FAQs
          </button>
          <h2 className="text-xl font-black text-slate-900 m-0">{selected.title}</h2>
          <div className="text-xs text-slate-400 font-semibold mt-1 mb-6">{selected.readMinutes} min read</div>
          <LearnBlocks blocks={selected.blocks} />
        </article>
      );
    }

    const pinnedId = library.nextBestModuleId;

    return (
      <div className="flex flex-col gap-10 w-full">
        {(library.tracks || []).map(track => {
          const trackModules = modules.filter(item => item.track === track.id);
          if (trackModules.length === 0) return null;

          return (
            // The What's new tour spotlights the first section that matches.
            <section key={track.id} className="flex flex-col gap-4" data-whats-new="faq-library">
              <div>
                <h2 className="text-base font-black text-slate-900 m-0">{track.title}</h2>
                <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">{track.description}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trackModules.map(module => (
                  <ModuleCard
                    key={module.id}
                    module={module}
                    onOpen={openModule}
                    pinned={module.id === pinnedId}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu selectedTab={'Learn'} />
        <div className="flex-1 px-4 py-6 md:px-8 w-full max-w-7xl mx-auto min-w-0">
          <h1 className="text-2xl font-black text-slate-900 m-0">FAQ</h1>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 mb-6 max-w-[68ch]">
            Short, practical answers on getting paid, getting tipped and getting reviewed.
          </p>
          {renderBody()}
        </div>
      </div>
    </>
  );
};

export default Learn;
